import React, { useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup } from '@atlaskit/button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { Box, xcss } from '@atlaskit/primitives';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header/src/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import Graph from '@atlassian/jira-graph/src/ui/index.tsx';
import type { header_softwareReleasesVersionDetail$key } from '@atlassian/jira-relay/src/__generated__/header_softwareReleasesVersionDetail.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import AsyncReleaseAutomationMenu from '@atlassian/jira-software-releases-automation-menu/src/async.tsx';
import { ManageWarningsModal } from '@atlassian/jira-software-releases-manage-warnings/src/ui/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary.tsx';
import { CONNECT_APP_EXPERIENCE_ID } from './connect-apps/constants.tsx';
import { ConnectApps } from './connect-apps/index.tsx';
import { AsyncEditableName } from './editable-name/async.tsx';
import { FeedbackButton } from './feedback-button/FeedbackButton.tsx';
import { ReleaseNotesButton } from './release-notes-button/index.tsx';

type VersionPageHeaderWrapperProps = {
	breadcrumbs?: ReactNode;
	children: ReactNode;
};

/**
 * This component is wraps header of the version detail page.
 *
 * When the gate is enabled, it applies a negative top margin to the header.
 */
const VersionPageHeaderWrapper = ({ breadcrumbs, children }: VersionPageHeaderWrapperProps) => {
	if (getWillShowNav4()) {
		return (
			<Box xcss={headerWrapperStyles}>
				<PageHeader breadcrumbs={breadcrumbs} truncateTitle disableTitleStyles>
					{children}
				</PageHeader>
			</Box>
		);
	}

	return (
		<PageHeader breadcrumbs={breadcrumbs} truncateTitle disableTitleStyles>
			{children}
		</PageHeader>
	);
};

export const Header = ({
	jiraVersion,
	shouldShowBreadcrumbs = true,
	shouldShowFeedbackButton = true,
	shouldShowAutomationButton = true,
	shouldShowGraphButton = true,
}: {
	jiraVersion: header_softwareReleasesVersionDetail$key;
	shouldShowBreadcrumbs?: boolean;
	shouldShowFeedbackButton?: boolean;
	shouldShowAutomationButton?: boolean;
	shouldShowGraphButton?: boolean;
}) => {
	const breadcrumbs = shouldShowBreadcrumbs ? <BreadcrumbRenderer /> : null;

	const onConnectAppExpFail = useExperienceFail({ experience: CONNECT_APP_EXPERIENCE_ID });
	const [warningsModalOpen, setWarningsModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const version = useFragment(
		graphql`
			fragment header_softwareReleasesVersionDetail on JiraVersion {
				id @required(action: THROW)
				allIssues: issues(filter: ALL) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				...async_softwareReleasesVersionDetail_AsyncEditableName
				...ui_softwareReleasesManageWarnings_ManageWarningsModal
				...connectApps_softwareReleasesVersionDetail
				...releaseNotesButton_softwareReleasesVersionDetail
			}
		`,
		jiraVersion,
	);

	const issues = version.allIssues.totalCount;

	return (
		<VersionPageHeaderWrapper breadcrumbs={breadcrumbs}>
			<HeaderTitleLane>
				<LeftContainer>
					<HeaderTitle data-testid="software-releases-version-detail.ui.header.title">
						<PageTitleContainer isOverflowEnabled={isEditing}>
							<AsyncEditableName
								isEditing={isEditing}
								setIsEditing={setIsEditing}
								jiraVersion={version}
							/>
						</PageTitleContainer>
					</HeaderTitle>
				</LeftContainer>
				<RightContainer>
					<ButtonGroup>
						{shouldShowGraphButton && <Graph from={version.id} />}
						{shouldShowFeedbackButton && (
							<NonCriticalErrorBoundary fallback="flag" eventLocation="feedbackButton">
								<FeedbackButton />
							</NonCriticalErrorBoundary>
						)}
						{shouldShowAutomationButton && (
							<NonCriticalErrorBoundary fallback="unmount" eventLocation="releaseAutomationMenu">
								<AsyncReleaseAutomationMenu />
							</NonCriticalErrorBoundary>
						)}
						<ModalTransition>
							{warningsModalOpen && (
								<NonCriticalErrorBoundary fallback="flag" eventLocation="warningConfigModal">
									<ManageWarningsModal
										jiraVersion={version}
										setModalClosed={() => {
											setWarningsModalOpen(false);
										}}
									/>
								</NonCriticalErrorBoundary>
							)}
						</ModalTransition>
						{!isDevopsFeatureDisabledInFedRamp() && issues > 0 && (
							<ReleaseNotesButton jiraVersion={version} />
						)}
					</ButtonGroup>
				</RightContainer>
			</HeaderTitleLane>
			<NonCriticalErrorBoundary
				fallback="flag"
				eventLocation="headerConnectApps"
				onError={onConnectAppExpFail}
			>
				<MarketPlaceAddonLane>
					<ConnectApps jiraVersion={version} />
				</MarketPlaceAddonLane>
			</NonCriticalErrorBoundary>
		</VersionPageHeaderWrapper>
	);
};

const headerWrapperStyles = xcss({
	marginBlockStart: 'space.negative.150',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderTitleLane = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	rowGap: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftContainer = styled.div({
	flexGrow: 1,
	marginLeft: token('space.negative.100'),
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightContainer = styled.div({
	flexGrow: 1,
	display: 'flex',
	justifyContent: 'flex-end',
	minWidth: '300px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MarketPlaceAddonLane = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderTitle = styled.div({
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
	height: '42px',
});

type PageTitleContainerProps = {
	isOverflowEnabled: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTitleContainer = styled.div({
	marginTop: token('space.negative.100'),
	minWidth: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	overflow: (props: PageTitleContainerProps) => (props.isOverflowEnabled ? 'visible' : 'hidden'),
});
